import { useId, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * ManagementLevelChecklist (TD-2105, Contact Enrichment Stream B)
 *
 * Collapsible, fixed 10-item management-level enum used by the Discover panel.
 * Selections combine with job titles using AND logic on the discover request
 * (sent as `managementLevels[]`).
 *
 * Controlled component: parent owns `value` (array of selected level values)
 * and receives the next selection through `onChange`.
 */

// `value` is the FE-internal key; `label` is what the user sees; `surfe` is the
// exact string sent in the discover request's `criteria.people.seniorities`
// (the BE forwards it verbatim to Surfe's /v2/people/search). Surfe's seniority
// enum uses "C-Level" for the C-suite; the rest match the labels. Unrecognised
// seniority values cause the provider to reject the entire search request
// (input_validation_failed) — TD-2891.
export const MANAGEMENT_LEVELS = [
  { value: 'owner', label: 'Owner', surfe: 'Owner' },
  { value: 'founder', label: 'Founder', surfe: 'Founder' },
  { value: 'c_suite', label: 'C suite', surfe: 'C-Level' },
  { value: 'partner', label: 'Partner', surfe: 'Partner' },
  { value: 'vp', label: 'VP', surfe: 'VP' },
  { value: 'head', label: 'Head', surfe: 'Head' },
  { value: 'director', label: 'Director', surfe: 'Director' },
  { value: 'manager', label: 'Manager', surfe: 'Manager' },
  { value: 'board_member', label: 'Board Member', surfe: 'Board Member' },
  { value: 'other', label: 'Other', surfe: 'Other' }
];

/** Map selected management-level values → Surfe seniority strings for the request. */
export const toSurfeSeniorities = (values = []) =>
  MANAGEMENT_LEVELS.filter((l) => values.includes(l.value)).map((l) => l.surfe);

// Inverse of the surfe mapping above, keyed case-insensitively. Used (TD-2497) to
// map a discovered contact's Surfe seniority (its `manager_function`) back to the
// management-level value so the discovered list can be filtered by, and the
// checklist auto-checked from, the levels people actually came back as.
const SURFE_TO_LEVEL = new Map(MANAGEMENT_LEVELS.map((l) => [l.surfe.toLowerCase(), l.value]));

/** Map a Surfe seniority string → management-level value; null for free-text/unknown. */
export const levelForSeniority = (seniority) =>
  seniority ? (SURFE_TO_LEVEL.get(String(seniority).trim().toLowerCase()) ?? null) : null;

const ManagementLevelChecklist = ({
  value = [],
  onChange,
  defaultCollapsed = false,
  // TD-2485: 'pills' renders the same enum as always-visible tap-toggle buttons
  // for the mobile Discover sheet (fewer vertical rows, larger touch targets, no
  // nested accordion). 'checklist' is the desktop popover default (unchanged).
  variant = 'checklist'
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const optionsId = `${useId()}-options`;

  const toggleLevel = (levelValue) => {
    if (value.includes(levelValue)) {
      onChange(value.filter((v) => v !== levelValue));
    } else {
      onChange([...value, levelValue]);
    }
  };

  const selectedCount = value.length;

  if (variant === 'pills') {
    return (
      // <fieldset> (native grouping element) rather than role="group" — a11y-
      // equivalent and Sonar-clean; aria-label supplies the group name.
      <fieldset className="management-level-pills" aria-label="Management level">
        {MANAGEMENT_LEVELS.map((level) => {
          const selected = value.includes(level.value);
          return (
            <button
              key={level.value}
              type="button"
              className={`management-level-pill ${selected ? 'is-selected' : ''}`}
              aria-pressed={selected}
              onClick={() => toggleLevel(level.value)}
            >
              {level.label}
            </button>
          );
        })}
      </fieldset>
    );
  }

  return (
    <div className="management-level-checklist">
      <button
        type="button"
        className="management-level-checklist-header"
        aria-expanded={!collapsed}
        aria-controls={optionsId}
        onClick={() => setCollapsed((prev) => !prev)}
      >
        <span className="management-level-checklist-title">
          Management level{selectedCount ? ` (${selectedCount})` : ''}
        </span>
        <span
          className={`management-level-checklist-chevron ${collapsed ? 'collapsed' : 'expanded'}`}
          aria-hidden="true"
        />
      </button>

      {!collapsed && (
        <div className="management-level-checklist-options" id={optionsId}>
          {MANAGEMENT_LEVELS.map((level) => (
            <label key={level.value} className="management-level-checklist-option">
              <input
                type="checkbox"
                checked={value.includes(level.value)}
                onChange={() => toggleLevel(level.value)}
              />
              <span>{level.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

ManagementLevelChecklist.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  defaultCollapsed: PropTypes.bool,
  variant: PropTypes.oneOf(['checklist', 'pills'])
};

export default ManagementLevelChecklist;
